/**
 * Search and link — the "no previous order recognised" state made fixable
 * (docs/features/price-precedent/02_journeys.md). The CSR searches this
 * customer's prior jobs and estimates and links one; the link becomes the
 * quote's precedent (a recorded manual match) from then on.
 *
 * Shared by the catalogue item page's price-level picker and the estimate
 * detail's precedent table, so the miss reads and fixes the same everywhere.
 */
import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import type { PrecedentCandidate } from "@shared-core/data/price-precedent-types";
import { formatPrecedentDate } from "@shared-core/domain/price-precedent";
import { formatQuotedPrice } from "@shared-core/domain/formatters";

export function PrecedentSearchAndLink({
  message = "No previous order recognised for this spec from this customer.",
  onSearch,
  onLink,
  linkError = null,
}: {
  /** The sentence the state opens with. */
  message?: string;
  onSearch: (query: string) => Promise<PrecedentCandidate[]>;
  onLink: (candidate: PrecedentCandidate) => void;
  /** Shown under the results when the last link attempt failed. */
  linkError?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<PrecedentCandidate[] | null>(null);
  const [searchFailed, setSearchFailed] = useState(false);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const handle = setTimeout(() => {
      onSearch(query).then(
        (r) => {
          if (cancelled) return;
          setSearchFailed(false);
          setResults(r);
        },
        () => {
          if (cancelled) return;
          setSearchFailed(true);
          setResults([]);
        },
      );
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [open, query, onSearch]);
  return (
    <div className="rounded-md border border-dashed border-border px-3 py-2 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-muted-foreground">{message}</span>
        {!open && (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            onClick={() => setOpen(true)}
          >
            <Search className="size-3.5" aria-hidden />
            Search and link a previous order
          </button>
        )}
      </div>
      {open && (
        <div className="mt-2 space-y-2">
          <input
            type="search"
            autoFocus
            aria-label="Search this customer's previous jobs and estimates"
            placeholder="Job number, estimate ref or description…"
            className="w-full min-w-0 rounded-md border border-border bg-background px-2 py-1 text-sm"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {results == null ? (
            <p className="text-xs text-muted-foreground">Searching…</p>
          ) : searchFailed ? (
            <p className="text-xs text-red-600">
              Search failed — try again in a moment.
            </p>
          ) : results.length === 0 ? (
            <p className="text-xs text-muted-foreground">Nothing matches.</p>
          ) : (
            <ul className="divide-y divide-border rounded-md border border-border">
              {results.map((c) => (
                <li
                  key={c.reference}
                  className="flex items-center justify-between gap-3 px-2 py-1.5"
                >
                  <span className="block min-w-0 flex-1">
                    <span className="font-medium">{c.reference}</span>
                    <span className="ml-1 text-xs text-muted-foreground">
                      {c.kind === "estimate" ? "estimate" : "job"} ·{" "}
                      {formatPrecedentDate(c.date)}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {c.quantity.toLocaleString()} @{" "}
                      {formatQuotedPrice(c.price_pence)} · {c.description}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="shrink-0 rounded-md border border-border px-2 py-1 text-xs font-medium hover:bg-muted"
                    onClick={() => onLink(c)}
                  >
                    Link as precedent
                  </button>
                </li>
              ))}
            </ul>
          )}
          {linkError && (
            <p role="alert" className="text-xs text-red-600">
              {linkError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
